import { Component, computed, effect, Input, OnDestroy, OnInit } from '@angular/core';
import { Offer } from 'ng2-app/services/offers/offer.model';
import { Approval } from 'ng2-app/models/dto/approval/approval.model';
import { OfferOption } from 'ng2-app/models/dto/offer-option/offer-option.model';
import { Store } from '@ngxs/store';
import { GetValuePropositions } from 'ng2-app/store/value-proposition/value-proposition.actions';
import { BehaviorSubject, combineLatest, Observable, Subject, takeUntil, map, take, of } from 'rxjs';
import { ValueProposition } from 'ng2-app/models/dto/value-proposition.model';
import { ValuePropositionState } from 'ng2-app/store/value-proposition/value-proposition.state';
import { LenderUserState } from 'ng2-app/store/lender-user/lender-user/lender-user.state';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { transition, trigger, useAnimation } from '@angular/animations';
import { fadeIn, fadeOut } from 'ng-animate';
import { DealOptionType } from 'ng2-app/models/dto/deal-option.model';
import { AttachValuePropsToApproval } from 'ng2-app/store/offers/approvals.actions';
import { AttachValuePropsToOffer } from 'ng2-app/store/offers/offer.actions';
import { AttachValuePropsToOption } from 'ng2-app/store/offers/offer-options.actions';
import { catchError } from 'rxjs/operators';

type FinancingOption = Offer | OfferOption | Approval;

@Component({
  selector: 'app-value-props-smart',
  templateUrl: './value-props-smart.component.html',
  styleUrls: ['./value-props-smart.component.scss'],
  animations: [
    trigger('listAnimation', [
      transition(':enter', useAnimation(fadeIn, {params: {timing: 0.3}})),
      transition(':leave', useAnimation(fadeOut, {params: {timing: 0.3}})),
    ]),
  ],
  standalone: false
})
export class ValuePropsSmartComponent implements OnInit, OnDestroy {
  @Input() set option(option: FinancingOption) {
    this._option = option;
    this.activeProps$.next(option.valuePropositions || []);
    if (!this.editMode$.getValue()) {
      this.selectedProps$.next(option.valuePropositions || []);
    }
  }
  get option() : FinancingOption {
    return this._option;
  }

  @Input() optionType : DealOptionType;

  valuePropositions$: Observable<ValueProposition[]> = this.store.select(ValuePropositionState.valuePropositions);
  isLoading$: Observable<boolean> = this.store.select(ValuePropositionState.loading);
  canSeePropositions = this.store.selectSignal( LenderUserState.hasPermission('financingOptionValueProposition.read') );
  canAttachPropositions = this.store.selectSignal( LenderUserState.hasPermission('financingOption.attach.valueProposition') );
  canSeeValuePropsComponent = computed(() => this.canSeePropositions() || this.canAttachPropositions());

  saving: boolean = false;

  activeProps$ = new BehaviorSubject<ValueProposition[]>([]);
  selectedProps$ = new BehaviorSubject<ValueProposition[]>([]);
  editMode$ = new BehaviorSubject<boolean>(false);
  destroyed$ = new Subject<boolean>();

  remainingOptions$: Observable<ValueProposition[]>;
  hasChanges$: Observable<boolean>;

  private _option: FinancingOption;

  constructor(
    private store: Store
  ) {
    effect(() => {
      if (this.canSeePropositions()) {
        this.store.dispatch(new GetValuePropositions());
      }
    });
  }

  ngOnInit() {
    this.remainingOptions$ = combineLatest([this.valuePropositions$, this.selectedProps$.asObservable()])
      .pipe(
        takeUntil(this.destroyed$),
        map(([allProps, selectedProps]) => {
          const selectedIds = [...new Set(selectedProps.map((valueProp) => valueProp.id))];
          return allProps.filter((valueProp) => !selectedIds.includes(valueProp.id));
        })
      );

    this.hasChanges$ = combineLatest([this.activeProps$.asObservable(), this.selectedProps$.asObservable()])
      .pipe(
        takeUntil(this.destroyed$),
        map(([activeProps, selectedProps]) => {
          const activeIds = activeProps.map((valueProp) => valueProp.id);
          const selectedIds = selectedProps.map((valueProp) => valueProp.id);
          return activeIds.join(',') !== selectedIds.join(',');
        })
      );
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  valuePropId(i, valueProp) {
    return valueProp.id;
  }

  onAddValueProp(valueProp: ValueProposition) {
    this._setEditMode(true);
    this.selectedProps$.next([...this.selectedProps$.getValue(), valueProp]);
  }

  onAddAll() {
    this._setEditMode(true);
    this.remainingOptions$.pipe(take(1)).subscribe((remainingProps) => {
      const currentProps = this.selectedProps$.getValue();
      this.selectedProps$.next([...currentProps, ...remainingProps]);
    });
  }

  onRemoveAll() {
    this._setEditMode(true);
    this.selectedProps$.next([]);
  }

  changeOrder(event: CdkDragDrop<string[]>) {
    const items = [...this.selectedProps$.getValue()];
    moveItemInArray(items, event.previousIndex, event.currentIndex);
    this.selectedProps$.next(items);
  }

  removeValueProp(valueProp: ValueProposition) {
    this.selectedProps$.next(this.selectedProps$.getValue().filter((item) => item.id !== valueProp.id));
  }

  onToggleEditMode(value: boolean) {
    this._setEditMode(value);
  }

  onSave() {
    const actionMap = {
      'approval': AttachValuePropsToApproval,
      'offer': AttachValuePropsToOffer,
      'option': AttachValuePropsToOption,
    };
    const action = actionMap[this.optionType];
    if (!action) {
      throw new Error(`Value props are not supported for ${this.optionType}`);
    }

    const valuePropositionIds = this.selectedProps$.getValue().map((valueProp) => valueProp.id);
    this.saving = true;
    this.store.dispatch(new action(this.option.id, valuePropositionIds)).pipe(take(1), catchError(() => {
      this.saving = false;
      return of();
    })).subscribe(() => {
      this.saving = false;
      this._setEditMode(false);
    });
  }


  private _setEditMode(val: boolean) {
    if (this.editMode$.getValue() === val) {
      return;
    }
    this.editMode$.next(val);
    if (!val) {
      const currentProps = (this.activeProps$.getValue() || []);
      this.selectedProps$.next([...currentProps]);
    }
  }
}
